import { ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faDownload, faStar, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { FilePreviewService } from '@reconcept-platform/shared/ui/core/file-preview';
import {
  ButtonComponent,
  ButtonContainerComponent,
  ButtonIconOnlyComponent,
  LinkButtonComponent,
} from '@reconcept-platform/shared/ui/standalone/button';
import { FilePreviewAudioComponent } from './file-preview-audio/file-preview-audio.component';
import { FilePreviewIframeComponent } from './file-preview-iframe/file-preview-iframe.component';
import { FilePreviewImageComponent } from './file-preview-image/file-preview-image.component';
import { FilePreviewVideoComponent } from './file-preview-video/file-preview-video.component';

@Component({
  selector: 'rcp-file-preview',
  styleUrls: ['./file-preview.component.scss'],
  host: {
    '[class.is-shown]': 'filePreviewService.filePreviewIsShown',
  },
  template: `
    <!-- overlay -->
    @if (this.filePreviewService.filePreviewIsShown) {
      <div class="overlay" (click)="hide()"></div>
    }

    <div class="close-container">
      <rcp-button-icon-only (click)="hide()" [icon]="faTimes" />
    </div>

    @if (!!filePreviewService.filePreviewOptions) {
      <div class="content" (click)="hide()">
        @switch (filePreviewService.filePreviewOptions.view) {
          @case ('image') {
            <rcp-file-preview-image
              [previewFileURL]="filePreviewService.filePreviewOptions.previewFileURL"
            ></rcp-file-preview-image>
          }
          @case ('iframe') {
            <rcp-file-preview-iframe
              [previewFileURL]="filePreviewService.filePreviewOptions.previewFileURL"
            ></rcp-file-preview-iframe>
          }
          @case ('audio') {
            <rcp-file-preview-audio
              [previewFileURL]="filePreviewService.filePreviewOptions.previewFileURL"
            ></rcp-file-preview-audio>
          }
          @case ('video') {
            <rcp-file-preview-video
              [previewFileURL]="filePreviewService.filePreviewOptions.previewFileURL"
            ></rcp-file-preview-video>
          }
          @case ('main') {
            <div class="preview-main">
              @if (filePreviewService.filePreviewOptions.previewType === 'unknown') {
                <p>
                  {{ 'qz-we-are-unable-to-preview-this-file-click-button-zm' | translate }}
                </p>
              }
              @if (filePreviewService.filePreviewOptions.previewType === 'pdf') {
                <p>
                  {{ 'qz-this-file-pdf-this-can-be-displayed-directly-in-zm' | translate }}
                </p>
              }
              @if (filePreviewService.filePreviewOptions.previewType === 'office') {
                <p>
                  {{ 'qz-this-file-microsoft-office-document-this-can-be-zm' | translate }}
                </p>
              }

              <rcp-button-container>
                @if (
                  filePreviewService.filePreviewOptions.previewType === 'pdf' ||
                  filePreviewService.filePreviewOptions.previewType === 'office'
                ) {
                  <rcp-button color="primary" (click)="clickShowHere($event)">
                    {{ 'qz-show-in-this-window-zm' | translate }}
                    <fa-icon [icon]="faStar"></fa-icon>
                  </rcp-button>
                }
                <rcp-link-button
                  [href]="filePreviewService.filePreviewOptions.originalFileURL"
                  target="external"
                  [icon]="faDownload"
                >
                  {{ 'qz-download-to-your-device-zm' | translate }}
                </rcp-link-button>
              </rcp-button-container>
            </div>
          }
        }
      </div>
    }
  `,
  imports: [
    FaIconComponent,
    FilePreviewImageComponent,
    FilePreviewIframeComponent,
    FilePreviewAudioComponent,
    FilePreviewVideoComponent,
    TranslatePipe,
    ButtonContainerComponent,
    ButtonComponent,
    LinkButtonComponent,
    ButtonIconOnlyComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilePreviewComponent {
  filePreviewService = inject(FilePreviewService);

  @ViewChild(FilePreviewAudioComponent)
  audioPlayer: FilePreviewAudioComponent;

  @ViewChild(FilePreviewVideoComponent)
  videoPlayer: FilePreviewVideoComponent;

  protected readonly faTimes = faTimes;
  protected readonly faDownload = faDownload;
  protected readonly faStar = faStar;

  hide() {
    this.filePreviewService.hideFilePreview();
    this.audioPlayer?.pause();
    this.videoPlayer?.pause();
  }

  clickShowHere($event: any) {
    // prevent hide through background click
    $event.stopPropagation();
    this.filePreviewService.filePreviewOptions.view = 'iframe';
  }
}
